import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES, COMPANY } from "@/i18n";

const H = ({ n, children }) => (
  <h2 className="font-head font-bold text-ink text-2xl md:text-3xl tracking-tight mt-14 mb-5 flex items-baseline gap-3">
    <span className="font-mono text-brand text-base">{n}</span>
    {children}
  </h2>
);

const P = ({ children }) => <p className="text-ink-2 leading-relaxed mb-4">{children}</p>;

const Table = ({ head, rows }) => (
  <div className="overflow-x-auto my-6 rounded-xl border border-line">
    <table className="w-full text-left border-collapse min-w-[560px]">
      <thead>
        <tr className="bg-bg-2">
          {head.map((h) => (
            <th key={h} className="font-head font-bold text-ink text-sm px-5 py-4 border-b border-line align-top">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-line last:border-b-0 align-top">
            {r.map((c, j) => (
              <td key={j} className={`px-5 py-4 text-sm ${j === 0 ? "font-medium text-ink" : "text-ink-2"}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Privacy() {
  const { lang } = useLang();
  const back = lang === "nl" ? "Terug naar home" : "Back to home";

  return (
    <div className="min-h-screen bg-bg">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-bg/80 backdrop-blur-xl border-b border-line">
        <div className="mx-auto max-w-[900px] px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="privacy-logo-home">
            <img src={IMAGES.logoFull} alt="CHEPL" className="h-12 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-ink-2 hover:text-brand transition-colors duration-300"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {back}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 lg:px-8 py-16 md:py-24" data-testid="privacy-page">
        <span className="font-mono text-xs tracking-[0.28em] uppercase text-brand">Privacy</span>
        <h1 className="font-head font-black tracking-tighter text-ink text-[clamp(2.25rem,6vw,4.5rem)] leading-[0.95] mt-4">
          Privacyverklaring
        </h1>
        <p className="text-ink-muted mt-4">Laatst bijgewerkt: juni 2026</p>

        <div className="mt-8 p-6 rounded-2xl border border-line bg-bg-2">
          <P>
            Deze privacyverklaring geldt voor de producten en platformen die CHEPL BV ontwikkelt en voert —
            waaronder <strong className="text-ink">Slackr</strong>, <strong className="text-ink">ScrollStory</strong>,{" "}
            <strong className="text-ink">TalentHub</strong> en alle overige voor klanten ontwikkelde producten.
            Hieronder vindt u de verklaring zoals die geldt voor ScrollStory. Voor onze overige producten hanteren
            wij dezelfde uitgangspunten; voor productspecifieke vragen kunt u contact met ons opnemen via{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-brand underline underline-offset-2">{COMPANY.email}</a>.
          </P>
        </div>

        <h2 className="font-head font-bold text-ink text-xl md:text-2xl tracking-tight mt-14">Privacyverklaring ScrollStory</h2>

        <H n="1.">Wie zijn wij?</H>
        <P>
          ScrollStory is een product (platform en app) van CHEPL BV, voor brandweerposten en hulpverleningsorganisaties:
          nieuws, roosters, beschikbaarheid, presentie, oefeningen en interne communicatie. ScrollStory is geen
          zelfstandige rechtspersoon; CHEPL BV is de partij die verantwoordelijk is voor de dienst. In deze verklaring
          betekent "wij" of "ScrollStory" daarom: CHEPL BV.
        </P>
        <P>
          <strong className="text-ink">CHEPL BV</strong><br />
          Burg. van der Heidelaan 43, 3451 ZS Vleuten<br />
          KvK: 59581883<br />
          E-mail: {COMPANY.email}
        </P>

        <H n="2.">Onze rol</H>
        <P>
          <strong className="text-ink">Verwerker:</strong> voor gegevens van leden van een post of organisatie werken wij
          in opdracht van die organisatie (bijv. de veiligheidsregio of post). Die organisatie is verwerkingsverantwoordelijke;
          hierover sluiten wij een verwerkersovereenkomst.
        </P>
        <P>
          <strong className="text-ink">Verwerkingsverantwoordelijke:</strong> voor onze eigen klantrelaties, de website,
          supportvragen en de technische werking van de app.
        </P>
        <P>
          Heb je een vraag over gegevens die je post bijhoudt? Neem dan eerst contact op met de beheerder van je post;
          wij helpen ook graag.
        </P>

        <H n="3.">Welke gegevens verwerken wij?</H>
        <Table
          head={["Categorie", "Voorbeelden", "Waarvoor"]}
          rows={[
            ["Accountgegevens", "Naam, e-mailadres, functie/rol, post(en), profielfoto, eventueel geboortedatum", "Inloggen, toegang en rechten per post"],
            ["Contactgegevens (optioneel)", "Mobiel nummer", "SMS-meldingen als je die zelf aanzet"],
            ["Planning en inzet", "Rooster, ploegdienst, indeling, aanwezigheid/presentie (incl. handtekening), taken, overdracht", "Organisatie van diensten en oefeningen"],
            ["Beschikbaarheid", "Vakantie, verlof, dienst, status 'ziek' of 'medisch' met datum en eventueel tijd", "Weten wie beschikbaar is. Wij vragen en bewaren géén diagnose of medische details."],
            ["Opleiding en oefening", "Bijscholing, verplichte oefeningen, inschrijvingen, beoordelingen", "Bijhouden van vakbekwaamheid"],
            ["Content", "Berichten, foto's, reacties, notulen, recepten, storingsmeldingen", "Interne communicatie"],
            ["Locatiegegevens", "Adres/straat bij een melding (bijv. wegafsluiting), GPS-gegevens uit een foto die je zelf uploadt", "Juist plaatsen van meldingen en routes"],
            ["Gezichtsherkenning (optioneel)", "Een wiskundige weergave van je gezicht (geen foto)", "Snel aan- en afmelden bij de kiosk. Alleen na jouw uitdrukkelijke toestemming en als je post dit aanzet."],
            ["Inloggegevens", "Wachtwoord (versleuteld), passkey/vingerafdruk-koppeling (biometrie blijft op je toestel), toestel-ID", "Veilig inloggen"],
            ["Pushmeldingen", "Push-token / toestel-ID", "Meldingen naar je telefoon sturen"],
            ["Technische gegevens", "IP-adres, apparaattype, foutmeldingen, logbestanden", "Beveiliging en probleemoplossing"],
            ["Spraak (optioneel)", "Spraakopnames die je zelf inspreekt", "Omzetten naar tekst; de opname wordt na verwerking niet bewaard"],
          ]}
        />
        <P>Wij verwerken geen gegevens voor advertentieprofielen en verkopen nooit gegevens aan derden.</P>

        <H n="4.">Grondslagen</H>
        <P>
          <strong className="text-ink">Overeenkomst / gerechtvaardigd belang van je organisatie:</strong> het organiseren
          van diensten, oefeningen en communicatie.
        </P>
        <P>
          <strong className="text-ink">Toestemming:</strong> voor gezichtsherkenning, SMS- en pushmeldingen en het delen
          van locatie of foto's. Je kunt toestemming altijd intrekken via de app (Instellingen) of via je beheerder.
        </P>
        <P>
          <strong className="text-ink">Wettelijke verplichting:</strong> waar de wet ons verplicht gegevens te bewaren of te verstrekken.
        </P>

        <H n="5.">Met wie delen wij gegevens?</H>
        <P>
          Binnen ScrollStory zien alleen leden en beheerders van je eigen post jouw gegevens, afhankelijk van hun rol.
          Daarnaast werken wij met zorgvuldig gekozen dienstverleners (subverwerkers), die alleen krijgen wat nodig is:
        </P>
        <Table
          head={["Dienst", "Doel"]}
          rows={[
            ["Emergent (cloudhosting)", "Hosting van app en database"],
            ["OneSignal", "Pushmeldingen naar de iOS- en Android-app"],
            ["Resend", "Versturen en ontvangen van e-mail"],
            ["Twilio", "SMS-meldingen (alleen als je die aanzet)"],
            ["OpenAI, Google (Gemini), Anthropic", "AI-functies zoals spraak-naar-tekst en samenvattingen. Gegevens worden niet gebruikt om hun modellen te trainen."],
            ["PDOK (Kadaster)", "Adressen opzoeken"],
            ["GraphHopper", "Routes berekenen"],
            ["Median.co", "Technische app-schil voor iOS en Android"],
            ["Apple en Google", "Distributie van de app en afleveren van pushmeldingen"],
          ]}
        />
        <P>
          Sommige partijen kunnen gegevens buiten de Europese Economische Ruimte verwerken. In dat geval zorgen wij voor
          passende waarborgen, zoals het EU-VS Data Privacy Framework of de standaardcontractbepalingen van de Europese Commissie.
        </P>

        <H n="6.">Hoe lang bewaren wij gegevens?</H>
        <ul className="list-disc pl-5 space-y-3 text-ink-2 leading-relaxed mb-4">
          <li><strong className="text-ink">Account:</strong> zolang je lid bent van een post. Verwijder je je account, dan worden je account, inloggegevens en toestellen direct verwijderd; overige persoonsgegevens binnen 30 dagen verwijderd of geanonimiseerd. Gedeelde inhoud (zoals foto's) kan voor de post blijven bestaan.</li>
          <li><strong className="text-ink">Gezichtsherkenning:</strong> tot je je toestemming intrekt, je account verwijdert of je post de functie uitzet; daarna direct verwijderd.</li>
          <li><strong className="text-ink">Afgewezen meldingen</strong> (zoals wegafsluitingen): maximaal 1 dag.</li>
          <li><strong className="text-ink">Roosters, presentie en opleidingshistorie:</strong> zolang je organisatie dit nodig heeft voor de verantwoording van vakbekwaamheid, met een maximum van 7 jaar.</li>
          <li><strong className="text-ink">Logbestanden:</strong> maximaal 90 dagen.</li>
        </ul>

        <H n="7.">Beveiliging</H>
        <P>
          Wij nemen passende technische en organisatorische maatregelen, waaronder versleutelde verbindingen (HTTPS),
          versleutelde opslag van wachtwoorden, rolgebaseerde toegang per post, inloggen met verificatiecode of passkey
          en strikte scheiding tussen posten.
        </P>

        <H n="8.">Jouw rechten</H>
        <P>
          Je hebt recht op inzage, correctie, verwijdering, beperking, overdraagbaarheid en bezwaar. Je kunt je account
          zelf verwijderen in de app via Meer → Account → Account verwijderen. Voor andere verzoeken mail je naar{" "}
          <a href={`mailto:${COMPANY.email}`} className="text-brand underline underline-offset-2">{COMPANY.email}</a>;
          wij reageren binnen een maand.
        </P>
        <P>
          Ben je het niet eens met hoe wij met je gegevens omgaan? Dan kun je een klacht indienen bij de Autoriteit Persoonsgegevens.
        </P>

        <H n="9.">Cookies en lokale opslag</H>
        <P>
          De app en website gebruiken alleen functionele opslag, zoals je inlogsessie, gekozen post, taal en thema.
          Wij gebruiken geen tracking- of advertentiecookies.
        </P>

        <H n="10.">Kinderen</H>
        <P>
          ScrollStory is bedoeld voor leden van hulpverleningsorganisaties. Voor jeugdbrandweerleden onder de 16 jaar is
          toestemming van een ouder of voogd nodig; de post regelt deze toestemming.
        </P>

        <H n="11.">Wijzigingen</H>
        <P>Wij kunnen deze verklaring aanpassen. Bij belangrijke wijzigingen laten wij dat weten via de app.</P>

        <H n="12.">Contact</H>
        <P>CHEPL BV – {COMPANY.email} – Burg. van der Heidelaan 43, 3451 ZS Vleuten</P>

        <div className="mt-16 pt-8 border-t border-line">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brand hover:text-brand-hover transition-colors duration-300"
            data-testid="privacy-bottom-back"
          >
            <ArrowLeft className="h-4 w-4" />
            {back}
          </Link>
        </div>
      </main>
    </div>
  );
}
